import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { useI18n } from '../context/I18nContext';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CheckoutPay() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [watch, setWatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    api.get(`/watches/${id}`)
      .then((r) => {
        if (r.data.availability === 'on_request') { navigate(`/watch/${id}`); return; }
        if (r.data.sold) { navigate(`/watch/${id}`); return; }
        setWatch(r.data);
      })
      .catch(() => navigate('/catalog'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const pay = async (e) => {
    e.preventDefault();
    if (!email) { toast.error(t('checkout.enter_email')); return; }
    setProcessing(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post('/checkout/session', {
        watch_id: watch.id, origin_url: origin, buyer_email: email, buyer_name: name,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
      setProcessing(false);
    }
  };

  if (loading) return <div className="max-w-[1440px] mx-auto px-6 py-32 text-center text-[#c9a961] font-serif text-2xl">Loading...</div>;
  if (!watch) return null;

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12 lg:py-16" data-testid="checkout-pay-page">
      <Link to={`/watch/${watch.id}`} className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#c9a961] hover:text-[#d8c180] mb-10" data-testid="checkout-back">
        <ArrowLeft size={14} /> {t('checkout.go_back')}
      </Link>

      <p className="gn-overline mb-4">{t('checkout.summary_t')}</p>
      <h1 className="font-serif text-4xl md:text-5xl text-[#f5f5f0] mb-12">{t('checkout.summary_t')}</h1>

      <div className="grid lg:grid-cols-12 gap-12">
        {/* SUMMARY */}
        <div className="lg:col-span-6 order-2 lg:order-1">
          <form onSubmit={pay} className="space-y-7" data-testid="checkout-form">
            <p className="text-base text-[#a3b5ad] font-light">{t('checkout.email_note')}</p>
            <div>
              <label className="gn-overline mb-3 block">{t('contact.name')}</label>
              <input required className="gn-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="checkout-name" />
            </div>
            <div>
              <label className="gn-overline mb-3 block">{t('contact.email')} *</label>
              <input required type="email" className="gn-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" data-testid="checkout-email" />
            </div>
            <button type="submit" disabled={processing || !email} className="gn-btn-primary w-full" data-testid="checkout-proceed">
              {processing && <Loader2 className="animate-spin" size={16} />}
              {t('checkout.proceed_pay')}
            </button>
            <p className="text-xs text-[#a3b5ad] tracking-wider text-center">
              {t('checkout.accepted')}
            </p>
          </form>
        </div>

        {/* WATCH CARD */}
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="gn-card">
            <div className="aspect-[4/3] overflow-hidden bg-[#05150f]">
              {watch.images?.[0] && <img src={watch.images[0]} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="p-6 border-t border-[#c9a961]/15">
              <p className="gn-overline mb-2">{watch.brand}</p>
              <h3 className="font-serif text-2xl text-[#f5f5f0] mb-1">{watch.model}</h3>
              <p className="text-xs text-[#a3b5ad] tracking-wider mb-6">{watch.reference}{watch.year ? ` · ${watch.year}` : ''}</p>
              <div className="flex items-baseline justify-between border-t border-[#c9a961]/10 pt-5">
                <span className="gn-overline">{t('checkout.total')}</span>
                <span className="font-serif text-3xl text-[#c9a961]" data-testid="checkout-total">
                  {new Intl.NumberFormat('it-CH', { style: 'currency', currency: watch.currency || 'EUR', maximumFractionDigits: 0 }).format(watch.price)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
